import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API_BASE_URL } from '../../envConfig';
import { Logger } from '../../utils/Logger';

export interface SSOProviderPublic {
    id: string;
    buttonName: string;
    providerType: string;
    loginPageHashPath: string;
}

/**
 * Non-sensitive Infralytiqs bootstrap values returned by
 * `/system/client/config`. Consumed by the in-browser Infralytiqs SDK so it
 * knows which tenant / site / ClickHouse DB to target.
 */
export interface InfralytiqsBootstrapPublic {
    tenantId: string;
    siteId: string;
    dbName: string;
    /**
     * If true, the in-page SDK requests `navigator.geolocation` consent on
     * page load and attaches precise lat/lng to every event. When false /
     * absent, the heatmap still works via server-side IP geolocation.
     */
    captureLocation?: boolean;
}

export interface ClientDomainConfig {
    domainKey: string;
    apiBaseUrl: string;
    brandLogo: { name: string; path: string };
    minifiedBrandLogo: { name: string; path: string };
    /** Data URLs for raster/vector logos per theme (from `/system/client/config`). */
    themeBrandLogos?: { light?: string; dark?: string };
    menuKey: string;
    /**
     * Per-domain theme pair. 'DEFAULT' (or absent) renders the original
     * LIGHT/DARK colour scheme; 'professional' renders the neutral variant.
     */
    theme?: string;
    /** Optional override for which built-in label dictionary to use (tier from hostname). */
    uiDictionaryKey?: 'campaignKing' | 'instantUpdate' | 'anamnesis' | 'infralytiqs';
    supportEmail: string;
    contactPhone: string;
    contactEmail: string;
    logLevel: string;
    facebookAppId: string;
    facebookApiVersion: string;
    fbEmbeddedRedirectUri: string;
    googleMapsBrowserJSApiKey: string;
    loginPageHashPath: string;
    /** Empty/absent: Login page uses built-in default Sign In legal line. */
    loginSignInTermsNotice?: string;
    /** When false, login page hides self-service Sign Up. Absent = enabled. */
    userSignUpEnabled?: boolean;
    /** When false, the "Manage Plan" sidebar menu is hidden. Absent = shown. */
    managePlan?: boolean;
    ssoProviders?: SSOProviderPublic[];
    infralytiqs?: InfralytiqsBootstrapPublic;
    /** Resolved server-side for Explorer published URLs: Mongo → CLIENT_DOMAIN_CONFIGS → ASSETS_S3_CDN_URL → default host. */
    assetsS3CdnUrl?: string;
}

interface ClientConfigState {
    data: ClientDomainConfig | null;
    loading: boolean;
    loaded: boolean;
    error: string | null;
}

const initialState: ClientConfigState = {
    data: null,
    loading: false,
    loaded: false,
    error: null,
};

async function fetchClientConfigByDomainKey(domainKey: string): Promise<ClientDomainConfig | null> {
    const url = `${API_BASE_URL}/system/client/config?domainKey=${encodeURIComponent(domainKey)}`;
    Logger.debug('clientConfigSlice', `Fetching client config from: ${url}`);

    try {
        const response = await fetch(url, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
        });

        if (!response.ok) {
            Logger.debug('clientConfigSlice', `Client config not found for domainKey: ${domainKey} (HTTP ${response.status})`);
            return null;
        }

        const result = await response.json();
        return result.data as ClientDomainConfig;
    } catch (err) {
        Logger.error('clientConfigSlice', `Client config request failed for domainKey: ${domainKey}: ${err}`);
        return null;
    }
}

export const fetchClientConfig = createAsyncThunk(
    'clientConfig/fetch',
    async (domainKey: string) => {
        const config = await fetchClientConfigByDomainKey(domainKey);

        if (!config) {
            throw new Error(`Failed to fetch client config for domainKey: ${domainKey}`);
        }

        return config;
    }
);

const clientConfigSlice = createSlice({
    name: 'clientConfig',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchClientConfig.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchClientConfig.fulfilled, (state, action) => {
                state.data = action.payload;
                state.loading = false;
                state.loaded = true;
                state.error = null;
            })
            .addCase(fetchClientConfig.rejected, (state, action) => {
                state.loading = false;
                state.loaded = true;
                state.error = action.error.message || 'Failed to fetch client config';
            });
    },
});

export default clientConfigSlice.reducer;
